import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { vendorAuthApi } from '../../api/endpoints/auth';

interface VendorContextType {
  vendor: { id: string; name: string; tenant_id: string } | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const VendorContext = createContext<VendorContextType | null>(null);

export function VendorAuthProvider({ children }: { children: ReactNode }) {
  const [vendor, setVendor] = useState<VendorContextType['vendor']>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await vendorAuthApi.me();
      if (data.authenticated && data.vendor) {
        setVendor(data.vendor);
      } else {
        setVendor(null);
      }
    } catch {
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (username: string, password: string) => {
    const data = await vendorAuthApi.login(username, password);
    if (data.success && data.vendor) {
      setVendor(data.vendor);
    } else {
      throw new Error(data.error || 'Login failed');
    }
  };

  const logout = async () => {
    await vendorAuthApi.logout();
    setVendor(null);
  };

  return (
    <VendorContext.Provider value={{ vendor, loading, login, logout }}>
      {children}
    </VendorContext.Provider>
  );
}

export function useVendorAuth() {
  const ctx = useContext(VendorContext);
  if (!ctx) throw new Error('useVendorAuth must be used within VendorAuthProvider');
  return ctx;
}

export function RequireVendorAuth() {
  const { vendor, loading } = useVendorAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!vendor) {
    return <Navigate to="/vendor/login" replace />;
  }

  return <Outlet />;
}
